/**
 * HQ 문의 채팅 API 클라이언트 (2026-07-27).
 * 백엔드: /api/v1/hq/support/*
 */
import hqApi, { HQ_TOKEN_KEY } from './hqApiClient';

export interface HqSupportThread {
  organization_id: string;
  store_name: string;
  last_message_at: string | null;
  last_message_by: 'owner' | 'hq' | null;
  last_message_text: string | null;
  unread: number;
  waiting_reply: boolean;
}

export interface HqSupportAttachment {
  id: string;
  filename?: string | null;
  mime?: string | null;
  size?: number | null;
}

export interface HqSupportMessage {
  id: string;
  author_type: 'owner' | 'hq';
  author_name: string | null;
  content: string;
  category: string | null;
  page_url: string | null;
  created_at: string | null;
  attachments: HqSupportAttachment[];
}

export const hqSupportService = {
  async listThreads(): Promise<HqSupportThread[]> {
    const { data } = await hqApi.get('/api/v1/hq/support/threads');
    return data.threads || [];
  },

  async getThread(orgId: string): Promise<{ store_name: string; messages: HqSupportMessage[] }> {
    const { data } = await hqApi.get(
      `/api/v1/hq/support/threads/${encodeURIComponent(orgId)}`,
    );
    return { store_name: data.store_name, messages: data.messages || [] };
  },

  async reply(orgId: string, content: string): Promise<void> {
    const form = new FormData();
    form.append('content', content);
    await hqApi.post(
      `/api/v1/hq/support/threads/${encodeURIComponent(orgId)}/messages`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  async waitingCount(): Promise<number> {
    try {
      const { data } = await hqApi.get('/api/v1/hq/support/unread-count');
      return data.waiting || 0;
    } catch {
      return 0;
    }
  },

  /** 답변을 전체 매장 공지로 승격 — 같은 질문 반복을 줄이는 장치 */
  async promoteToNotice(title: string, body: string): Promise<void> {
    await hqApi.post('/api/v1/hq/support/promote-notice', {
      title,
      body,
      show_dashboard: true,
      show_popup: false,
    });
  },

  /** 대화 중 진짜 버그인 건만 오류 게시판으로 올린다 (본부 판단) */
  async promoteToErrorReport(
    orgId: string,
    title: string,
    content: string,
  ): Promise<string> {
    const { data } = await hqApi.post('/api/v1/hq/support/promote-error', {
      organization_id: orgId,
      title,
      content,
      category: '문의에서 등록',
    });
    return data.report_id;
  },

  /** 첨부 이미지 blob URL (인증 필요해서 fetch 로 받아옴) */
  async attachmentUrl(attachmentId: string): Promise<string | null> {
    try {
      const base = (hqApi.defaults.baseURL || '').replace(/\/$/, '');
      const res = await fetch(`${base}/api/v1/hq/support/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(HQ_TOKEN_KEY)}` },
      });
      if (!res.ok) return null;
      return URL.createObjectURL(await res.blob());
    } catch {
      return null;
    }
  },
};

export default hqSupportService;
